import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name?: string;
    startDate?: string;
    endDate?: string;
    duration?: number;
  }) => void;
  isSubmitting: boolean;
}

export default function SprintCreateModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}: Props) {
  const [name, setName] = useState('Sprint');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [duration, setDuration] = useState<number | null>(null);

  // 주기 선택 → 종료일 자동 계산
  useEffect(() => {
    if (duration && startDate) {
      const end = new Date(startDate);
      end.setDate(end.getDate() + duration * 7);
      setEndDate(end.toISOString().split('T')[0]);
    }
  }, [duration, startDate]);

  // 종료일 직접 입력 → 주기 해제
  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    setDuration(null);
  };

  const handleSubmit = () => {
    onSubmit({
      name: name.trim() || undefined,
      startDate: startDate ? new Date(startDate).toISOString() : undefined,
      endDate: endDate ? new Date(endDate).toISOString() : undefined,
      duration: duration ?? undefined,
    });
  };

  // 모달 열릴 때 상태 초기화
  useEffect(() => {
    if (isOpen) {
      setName('Sprint');
      setStartDate('');
      setEndDate('');
      setDuration(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">스프린트 생성</h3>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1 hover:bg-gray-100 rounded-lg"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          {/* 제목 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              스프린트 제목
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sprint"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-sm text-gray-500 mt-1">
              입력한 제목 뒤에 자동 번호가 붙습니다 (예: Sprint 1, Sprint 2...)
            </p>
          </div>

          {/* 기간 설정 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              기간 설정 <span className="text-gray-400 font-normal">(선택)</span>
            </label>
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-gray-400">~</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleEndDateChange(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm text-gray-500">또는 주기:</span>
              <select
                value={duration ?? ''}
                onChange={(e) =>
                  setDuration(e.target.value ? parseInt(e.target.value) : null)
                }
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">선택 안함</option>
                <option value="1">1주</option>
                <option value="2">2주</option>
                <option value="3">3주</option>
                <option value="4">4주</option>
              </select>
            </div>
          </div>
        </div>

        {/* 버튼 */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            생성
          </button>
        </div>
      </div>
    </div>
  );
}
